import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ChevronDown, Download, Sparkles, TrendingUp, TrendingDown, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtXOF = (n) => {
  if (!n || !isFinite(n)) return "0 FCFA";
  return new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA";
};

/**
 * Calculateur de mensualités utilisable sur le site public (tpco.sn et vente.tpco.sn).
 *
 * Props:
 *  - initialPrix  : prix par défaut
 *  - autoOpen     : ouvert au montage
 *  - compact      : version compacte (pour intégration dans une page produit)
 *  - bienRef      : référence du bien à attacher au prospect (ex: "Almadies Tower · Appt 302")
 *  - uniteId / immeubleId : ids facultatifs pour rattacher le lead
 */
export default function MortgageCalculator({
  initialPrix = 50_000_000,
  autoOpen = false,
  compact = false,
  bienRef = "",
  uniteId = null,
  immeubleId = null,
}) {
  const [open, setOpen] = useState(autoOpen);
  const [prix, setPrix] = useState(initialPrix);
  const [apport, setApport] = useState(Math.round(initialPrix * 0.2));
  const [duree, setDuree] = useState(15); // années
  const [taux, setTaux] = useState(7.5); // %
  const [mode, setMode] = useState("constant"); // "constant" | "degressif"

  const [sim, setSim] = useState(null);
  const [computing, setComputing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Lead capture
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ nom: "", telephone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Recalcule à chaque changement
  useEffect(() => {
    const ctrl = new AbortController();
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        setComputing(true);
        const { data } = await axios.post(
          `${BACKEND_URL}/api/credit/simulate`,
          {
            prix_total: Number(prix),
            apport_initial: Number(apport),
            duree_mois: Math.max(1, Math.round(Number(duree) * 12)),
            taux_annuel: Number(taux),
            mode,
          },
          { signal: ctrl.signal }
        );
        setSim(data);
      } catch (e) {
        if (!axios.isCancel(e)) setSim(null);
      } finally {
        setComputing(false);
      }
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [open, prix, apport, duree, taux, mode]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/credit/simulate/pdf`,
        {
          simulation: {
            prix_total: Number(prix),
            apport_initial: Number(apport),
            duree_mois: Math.max(1, Math.round(Number(duree) * 12)),
            taux_annuel: Number(taux),
            mode,
          },
          client: { nom: lead.nom, telephone: lead.telephone, email: lead.email, bien_ref: bienRef },
        },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `simulation_credit_tpco.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* silent */ }
    finally { setDownloading(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/credit/leads`, {
        nom: lead.nom,
        telephone: lead.telephone,
        email: lead.email,
        message: lead.message,
        bien_ref: bienRef,
        unite_id: uniteId,
        immeuble_id: immeubleId,
        simulation: {
          prix_total: Number(prix),
          apport_initial: Number(apport),
          duree_mois: Math.max(1, Math.round(Number(duree) * 12)),
          taux_annuel: Number(taux),
          mode,
        },
      });
      setSent(true);
      // Téléchargement automatique du PDF en bonus
      setTimeout(() => downloadPdf(), 400);
    } catch (err) {
      alert("Erreur lors de l'envoi. Réessayez ou contactez-nous.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className={compact ? "bg-white" : "bg-[#F8FAFC] py-16 lg:py-20"} data-testid="mortgage-calc">
      <div className={compact ? "" : "max-w-[1100px] mx-auto px-6 lg:px-10"}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="bg-white border border-[#E2E5F1] rounded-2xl overflow-hidden shadow-sm"
        >
          {/* Header toggle */}
          <button
            onClick={() => setOpen(!open)}
            className="w-full flex items-center justify-between p-6 lg:p-8 text-left hover:bg-[#F8FAFC] transition"
            data-testid="mortgage-toggle"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#E91E63] flex items-center justify-center shrink-0">
                <Calculator className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#E91E63] font-bold">// SIMULATION</div>
                <h3 className="font-display text-xl lg:text-2xl font-bold text-[#0F1A3D] mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Calculez votre mensualité
                </h3>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-[#4B5775] transition-transform ${open ? "rotate-180" : ""}`} />
          </button>

          {open && (
            <div className="px-6 lg:px-8 pb-8 border-t border-[#F1F3FB]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6">
                {/* Inputs */}
                <div className="space-y-5">
                  <Input label="Prix du bien" value={prix} onChange={setPrix} suffix="FCFA" min={1_000_000} step={500_000} testid="calc-prix" />
                  <Input label="Apport personnel" value={apport} onChange={setApport} suffix="FCFA" min={0} step={100_000} testid="calc-apport" />
                  <Slider label="Durée du crédit" value={duree} onChange={setDuree} min={5} max={25} step={1} suffix=" ans" testid="calc-duree" />
                  <Slider label="Taux d'intérêt annuel" value={taux} onChange={setTaux} min={3} max={15} step={0.1} suffix=" %" testid="calc-taux" />

                  {/* Mode */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold block mb-1.5">Mode d'amortissement</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "constant", l: "Mensualités constantes", ic: TrendingUp },
                        { v: "degressif", l: "Dégressives", ic: TrendingDown },
                      ].map((m) => {
                        const Ic = m.ic;
                        const sel = mode === m.v;
                        return (
                          <button
                            key={m.v}
                            type="button"
                            onClick={() => setMode(m.v)}
                            className={`p-3 rounded-lg border-2 text-left transition flex items-center gap-2 ${sel ? "border-[#1E3A8A] bg-[#E8EBFA]" : "border-[#E2E5F1] hover:border-[#1E3A8A]/40"}`}
                            data-testid={`calc-mode-${m.v}`}
                          >
                            <Ic className={`w-4 h-4 ${sel ? "text-[#1E3A8A]" : "text-[#4B5775]"}`} />
                            <span className="text-xs font-bold text-[#0F1A3D]">{m.l}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="bg-gradient-to-br from-[#0F1A3D] to-[#1E3A8A] rounded-xl p-6 lg:p-8 text-white flex flex-col">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 font-bold mb-2 flex items-center gap-2">
                    Mensualité estimée
                    {computing && <Loader2 className="w-3 h-3 animate-spin text-white/40" />}
                  </div>
                  {!sim ? (
                    <div className="font-display text-4xl lg:text-5xl font-extrabold leading-none opacity-30" style={{ fontFamily: "'Playfair Display', serif" }}>—</div>
                  ) : sim.mode === "constant" ? (
                    <>
                      <div className="font-display text-4xl lg:text-5xl font-extrabold leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {fmtXOF(sim.mensualite)}
                      </div>
                      <div className="text-xs text-white/60 mt-2">par mois sur {duree} ans</div>
                    </>
                  ) : (
                    <>
                      <div className="font-display text-2xl lg:text-3xl font-extrabold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {fmtXOF(sim.mensualite_premiere)} <span className="text-white/40 mx-1">→</span> {fmtXOF(sim.mensualite_derniere)}
                      </div>
                      <div className="text-xs text-white/60 mt-2">décroissante · 1ère vs dernière échéance</div>
                    </>
                  )}

                  <div className="grid grid-cols-2 gap-4 mt-6 pt-5 border-t border-white/10">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Capital emprunté</div>
                      <div className="font-bold text-base mt-1">{fmtXOF(sim?.capital_emprunte)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Coût du crédit</div>
                      <div className="font-bold text-base mt-1 text-[#E91E63]">{fmtXOF(sim?.cout_credit)}</div>
                    </div>
                  </div>

                  {/* CTAs */}
                  <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => setShowLead(true)}
                      className="flex-1 h-11 rounded-lg bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
                      data-testid="calc-lead-btn"
                    >
                      <Sparkles className="w-4 h-4" /> Être rappelé(e)
                    </button>
                    <button
                      type="button"
                      onClick={downloadPdf}
                      disabled={!sim || downloading}
                      className="flex-1 h-11 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="calc-pdf-btn"
                    >
                      {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Télécharger PDF
                    </button>
                  </div>

                  <p className="text-[11px] text-white/40 mt-5 leading-relaxed">
                    * Simulation indicative — ne constitue pas une offre de prêt. Contactez-nous pour une étude personnalisée.
                  </p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* === MODAL LEAD CAPTURE === */}
      {showLead && (
        <div className="fixed inset-0 z-[60] bg-[#0F1A3D]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !sending && setShowLead(false)} data-testid="lead-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 lg:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {sent ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#E5F0E8] flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-[#2D7A3F]" />
                </div>
                <h3 className="font-display text-xl font-bold text-[#0F1A3D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Demande envoyée !</h3>
                <p className="text-sm text-[#4B5775] mb-5">Un conseiller TPCO vous contactera très prochainement. Votre simulation au format PDF est en cours de téléchargement.</p>
                <button onClick={() => { setShowLead(false); setSent(false); setLead({ nom: "", telephone: "", email: "", message: "" }); }} className="h-10 px-5 rounded-md bg-[#1E3A8A] text-white text-xs font-bold uppercase tracking-wider" data-testid="lead-close">Fermer</button>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-1">// CONSEILLER TPCO</div>
                  <h3 className="font-display text-xl font-bold text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Recevez votre simulation personnalisée
                  </h3>
                  <p className="text-xs text-[#4B5775] mt-1">Laissez vos coordonnées : nous vous rappelons sous 24h avec votre PDF.</p>
                </div>
                <input required value={lead.nom} onChange={(e) => setLead({ ...lead, nom: e.target.value })} placeholder="Nom & prénom *" className="w-full h-11 px-3 rounded-md border border-[#E2E5F1] focus:border-[#1E3A8A] focus:outline-none text-sm" data-testid="lead-nom" />
                <input required value={lead.telephone} onChange={(e) => setLead({ ...lead, telephone: e.target.value })} placeholder="Téléphone (WhatsApp) *" className="w-full h-11 px-3 rounded-md border border-[#E2E5F1] focus:border-[#1E3A8A] focus:outline-none text-sm" data-testid="lead-tel" />
                <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} type="email" placeholder="Email (optionnel)" className="w-full h-11 px-3 rounded-md border border-[#E2E5F1] focus:border-[#1E3A8A] focus:outline-none text-sm" data-testid="lead-email" />
                <textarea value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} placeholder="Précisez votre demande (optionnel)" rows={3} className="w-full px-3 py-2 rounded-md border border-[#E2E5F1] focus:border-[#1E3A8A] focus:outline-none text-sm resize-none" />

                {sim && (
                  <div className="bg-[#F8FAFC] border border-[#E2E5F1] rounded-lg p-3 text-xs">
                    <div className="text-[10px] uppercase tracking-wider text-[#9CA3B5] font-bold mb-1">Votre simulation</div>
                    <div className="font-mono text-[#0F1A3D]"><b>{fmtXOF(sim.mode === "constant" ? sim.mensualite : sim.mensualite_premiere)}</b> / mois · {fmtXOF(prix)} · {duree} ans · {taux}%</div>
                    {bienRef && <div className="text-[10px] text-[#4B5775] mt-1">{bienRef}</div>}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowLead(false)} className="flex-1 h-11 rounded-md border border-[#E2E5F1] text-xs font-bold uppercase tracking-wider text-[#4B5775]" data-testid="lead-cancel">Annuler</button>
                  <button type="submit" disabled={sending} className="flex-1 h-11 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="lead-submit">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Envoyer
                  </button>
                </div>
                <p className="text-[10px] text-[#9CA3B5] text-center">En soumettant ce formulaire, vous acceptez d'être recontacté(e) par TPCO Sénégal.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Input({ label, value, onChange, suffix, min, step, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold block mb-1.5">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value}
          min={min}
          step={step}
          onChange={(e) => onChange(Math.max(min, parseFloat(e.target.value) || 0))}
          className="w-full h-12 px-3 pr-16 text-base font-bold rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] text-[#0F1A3D]"
          data-testid={testid}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#9CA3B5] uppercase">{suffix}</span>
      </div>
    </div>
  );
}

function Slider({ label, value, onChange, min, max, step, suffix, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold">{label}</label>
        <span className="font-bold text-sm text-[#1E3A8A]">{value}{suffix}</span>
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 rounded-full appearance-none bg-[#E2E5F1] accent-[#E91E63] cursor-pointer"
        data-testid={testid}
      />
      <div className="flex justify-between text-[10px] text-[#9CA3B5] mt-1">
        <span>{min}{suffix}</span>
        <span>{max}{suffix}</span>
      </div>
    </div>
  );
}
